import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { isEmpty } from 'lodash'
import classNames from 'classnames'
import smoothscroll from 'smoothscroll-polyfill';

import useScrollPosition from '../../hooks/useScrollPosition'

import { ReactComponent as CheckBoxEmpty } from '../../assets/icons/checkBoxEmpty.svg'
import { ReactComponent as CheckBoxSelected } from '../../assets/icons/checkBoxSelected.svg'
import { ReactComponent as PenEditIcon } from '../../assets/icons/pen_edit_icon.svg'
import { ReactComponent as SelectedVectorIcon } from '../../assets/icons/selected-vector.svg'
import Loader from '../Loader'
import Pagination from '../Pagination/Pagination'
import SuiteRow from '../Suite/SuiteRow'
import usePrevious from '../../hooks/usePrevious'

const SuitesEventDetails = ({
  currentPersistedPaginationPage,
  currentVenueUuid,
  employeeRoles,
  event,
  eventId,
  history,
  isLoading,
  loadAllCustomerOrderingPreferencesByEventId,
  loadAttendantSettings,
  loadCustomers,
  loadPreOrders,
  loadRevenueCenters,
  loadSalesEvents,
  loadSuitesStaff,
  onMount,
  offsetToScroll,
  paginator,
  preOrders,
  setCurrentPaginationPage,
  selectedSuites,
  setSelectedSuites,
  setScreenScrollOffset,
}) => {
  const scrollPosition = useScrollPosition()
  const footerClasses = classNames('footer', {
    '-edit-mode': !isEmpty(selectedSuites),
  })

  const handlePersistPaginationPage = page => {
    setCurrentPaginationPage({ screen: 'eventDetails', page })
  }

  const setEventDetailsScrollPosition = () => {
    setScreenScrollOffset({ screen: 'eventDetails', offset: scrollPosition })
  }

  const { result } = paginator
  const numberOfSelectedSuites = Object.keys(selectedSuites).length
  const numberOfResultWithSuites = result.length

  const previousCurrentVenueUuid = usePrevious(currentVenueUuid)

  useEffect(() => {
    smoothscroll.polyfill()
    window.scrollTo({ top: offsetToScroll })
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    setScreenScrollOffset({ screen: 'eventDetails', offset: 0 })
    window.scrollTo({ top: offsetToScroll })
    // eslint-disable-next-line
  }, [currentPersistedPaginationPage])

  useEffect(() => {
    if (currentVenueUuid === previousCurrentVenueUuid) return
    if (!isEmpty(currentVenueUuid)) {
      loadSalesEvents(currentVenueUuid)
    }
    // eslint-disable-next-line
  }, [loadSalesEvents, currentVenueUuid])

  useEffect(() => {
    if (currentVenueUuid === previousCurrentVenueUuid) return
    if (!isEmpty(currentVenueUuid)) {
      loadSuitesStaff(currentVenueUuid)
    }
    // eslint-disable-next-line
  }, [loadSuitesStaff, currentVenueUuid])

  useEffect(() => {
    if (currentVenueUuid === previousCurrentVenueUuid && !isLoading) return
    if (!isEmpty(currentVenueUuid)) {
      loadPreOrders(currentVenueUuid)
    }
    // eslint-disable-next-line
  }, [loadPreOrders, currentVenueUuid])

  useEffect(() => {
    if (currentVenueUuid === previousCurrentVenueUuid) return
    loadRevenueCenters(currentVenueUuid)
    // eslint-disable-next-line
  }, [loadRevenueCenters, currentVenueUuid])

  useEffect(() => {
    if (currentVenueUuid === previousCurrentVenueUuid) return

    if (!isEmpty(currentVenueUuid)) {
      loadCustomers(currentVenueUuid)
    }
    // eslint-disable-next-line
  }, [currentVenueUuid, loadCustomers])

  useEffect(() => {
    if (currentVenueUuid === previousCurrentVenueUuid) return
    loadAllCustomerOrderingPreferencesByEventId()
    // eslint-disable-next-line
  }, [loadAllCustomerOrderingPreferencesByEventId, currentVenueUuid])

  useEffect(() => {
    if (currentVenueUuid === previousCurrentVenueUuid) return

    if (!isEmpty(currentVenueUuid)) {
      loadAttendantSettings(currentVenueUuid)
    }
    // eslint-disable-next-line
  }, [loadAttendantSettings, currentVenueUuid])

  useEffect(() => {
    if (currentVenueUuid === previousCurrentVenueUuid && !isLoading) return

    onMount(currentVenueUuid, eventId)
    // eslint-disable-next-line
  }, [onMount, currentVenueUuid, eventId])

  const renderButtonsToEditSelectedSuite = () => {
    if (isEmpty(selectedSuites)) return null

    return (
      <div className='edit-container'>
        <span className="edit-container__quantity">{numberOfSelectedSuites}</span>
        <div className="edit-container__buttons">
          <button
            className="edit-button"
            onClick={() => {
              history.push(`/suites/edit/${eventId}/customers`)
              setEventDetailsScrollPosition()
            }}
          >
            Edit Customers
            <span className='svg-wrapper'>
              <PenEditIcon />
            </span>
          </button>
          <button
            className="edit-button"
            onClick={() => {
              history.push(`/suites/edit/${eventId}/attendants`)
              setEventDetailsScrollPosition()
            }}
          >
            Edit Attendants
            <span className='svg-wrapper'>
              <PenEditIcon />
            </span>
          </button>
        </div>
      </div>
    )
  }

  const toggleSelectAllSuites = () => {
    let newObjectWithSelectedSuites = {}

    if (numberOfSelectedSuites === numberOfResultWithSuites) {
      setSelectedSuites()
    } else {
      result.forEach((suite) => {
        newObjectWithSelectedSuites = {...newObjectWithSelectedSuites, [suite.rvcUuid]: suite.rvcName}
      })

      setSelectedSuites(newObjectWithSelectedSuites)
    }
  }

  const renderCheckboxContainerWithIcon = () => {
    const checkboxIcon = numberOfSelectedSuites === numberOfResultWithSuites
      ? (
          <>
            <CheckBoxSelected />
            <SelectedVectorIcon className='vector-icon'/>
          </>
      )
      : <CheckBoxEmpty />

    if (!isEmpty(result) && !event.locked) {
      return (
        <div className="checkbox-container" onClick={toggleSelectAllSuites}>
          {checkboxIcon}
        </div>
      )
    }
  }

  const renderSuitesRows = () => (
    result.map((suite) => (
      <SuiteRow
        setEventDetailsScrollPosition={setEventDetailsScrollPosition}
        preOrders={preOrders}
        disabled={event.locked}
        employeeRoles={employeeRoles}
        eventId={eventId}
        history={history}
        key={suite.rvcUuid}
        selectedSuites={selectedSuites}
        setSelectedSuites={setSelectedSuites}
        suite={suite}
      />))
  )

  if (isEmpty(event)) return null

  return (
    <>
      {isLoading && <Loader />}
      <div className='event-details'>
        <div className='subtitle-row'>
          <div className="event-date-printer-and-toggle">
          </div>
          <div className='event-details__suites-header'>
            <span className='header-suite'>
              {renderCheckboxContainerWithIcon()}
              Suite
            </span>
            <span className='header-suite -customer'>Customers</span>
            <span className='header-suite -attendant'>Attendants</span>
          </div>
        </div>
        <div className='event-details__suites'>
          <div className="suite-rows">
            {renderSuitesRows()}
          </div>
          <div className={footerClasses}>
            {renderButtonsToEditSelectedSuite()}
            <Pagination
              paginator={paginator}
              handlePersistPaginationPage={handlePersistPaginationPage}
              currentPersistedPaginationPage={currentPersistedPaginationPage}
            />
          </div>
        </div>
      </div>
    </>
  )
}

SuitesEventDetails.defaultProps = {
  currentVenueUuid: '',
  event: {},
  eventId: '',
  isLoading: false,
  loadAllCustomerOrderingPreferencesByEventId: () => {},
  loadAttendantSettings: () => {},
  loadCustomers: () => {},
  loadPreOrders: () => {},
  loadRevenueCenters: () => {},
  loadSalesEvents: () => {},
  loadSuitesStaff: () => {},
  nextPage: () => {},
  previousPage: () => {},
  updateOffset: () => {},
  setCurrentPaginationPage: () => {},
  offsetToScroll: 0,
}

SuitesEventDetails.propTypes = {
  currentVenueUuid: PropTypes.string,
  event: PropTypes.object,
  eventId: PropTypes.string,
  isLoading: PropTypes.bool,
  loadAllCustomerOrderingPreferencesByEventId: PropTypes.func,
  loadAttendantSettings: PropTypes.func,
  loadCustomers: PropTypes.func,
  loadPreOrders: PropTypes.func,
  loadRevenueCenters: PropTypes.func,
  loadSalesEvents: PropTypes.func,
  loadSuitesStaff: PropTypes.func,
  nextPage: PropTypes.func,
  previousPage: PropTypes.func,
  updateOffset: PropTypes.func,
  setCurrentPaginationPage: PropTypes.func,
  offsetToScroll: PropTypes.number,
}

export default SuitesEventDetails
